import { type Dayjs } from 'dayjs';
import { concat, isFinite, isNil, uniqBy } from 'lodash/fp';
import { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { useNavigate } from 'react-router';
// eslint-disable-next-line max-len
import { type MobilityApplicationEntrypointContextDataFragment } from '../../../../api/fragments/MobilityApplicationEntrypointContextData';
import { type MobilityStockPublicListFragment } from '../../../../api/fragments/MobilityStockPublicList';
import {
    type GetMobilityVehicleFiltersQueryHookResult,
    useGetMobilityVehicleFiltersQuery,
} from '../../../../api/queries/getMobilityVehicleFilters';
import { useListMobilityStocksQuery } from '../../../../api/queries/listMobilityStocks';
import {
    SortingOrder,
    type LocalVariantFilteringRule,
    InventoryKind,
    type StockSortingRule,
    StockSortingField,
} from '../../../../api/types';
import PortalLoadingElement from '../../../../components/PortalLoadingElement';

export type PageState = {
    page: number;
    pageSize: number;
};

export type Period = {
    start: Dayjs | null;
    end: Dayjs | null;
};

export type MobilityStock = MobilityStockPublicListFragment;

export type MobilityVehicleFilters = GetMobilityVehicleFiltersQueryHookResult['data']['filters'];

export type State = PageState & {
    sort: StockSortingRule;
    modelFilter: string[];
    submodelFilter: string[];
    rangeVehiclePricesFilter: string[];
    filter: LocalVariantFilteringRule;
    scroll: boolean;
    stocks: MobilityStock[];

    /* Open or Close filter drawer. will has no effect if [showFilterDrawer] is false. */
    openFilterDrawer: boolean;

    /* Controls filter drawer's visibility */
    showFilterDrawer: boolean;

    total: number;
    filters: MobilityVehicleFilters;
    rentalPeriod:
        | {
              start: string | Date;
              end: string | Date;
          }
        | Period;
    startTime: string | Date;
    endTime: string | Date;
};

export type SetSortAction = { type: 'setSort'; sortBy: StockSortingRule };
export type SetFilterAction = { type: 'setFilter'; filteringRule: LocalVariantFilteringRule };

export type SetPageAction = { type: 'setPage'; page: number };
export type SetPageSizeAction = { type: 'setPageSize'; pageSize: number };
export type setUpdatePage = { type: 'setScroll'; scroll: boolean };
export type setStocks = { type: 'setStocks'; stocks: MobilityStock[] };
export type setOpenFilterDrawer = { type: 'setOpenFilterDrawer'; openFilterDrawer: boolean };
export type setShowFilterDrawer = { type: 'setShowFilterDrawer'; showFilterDrawer: boolean };
export type setTotal = { type: 'setTotal'; total: number };
export type setFilters = { type: 'setFilters'; filters: MobilityVehicleFilters };
export type setModelFilter = { type: 'setModelFilter'; modelFilter: string[] };
export type setSubmodelFilter = { type: 'setSubmodelFilter'; submodelFilter: string[] };
export type setRangeVehiclePricesFilter = {
    type: 'setRangeVehiclePricesFilter';
    rangeVehiclePricesFilter: string[];
};
export type setRentalPeriod = {
    type: 'setRentalPeriod';
    rentalPeriod: {
        start: string | Date;
        end: string | Date;
    };
};
export type setStartTime = {
    type: 'setStartTime';
    startTime: string | Date;
};

export type setEndTime = {
    type: 'setEndTime';
    endTime: string | Date;
};

export type Action =
    | SetPageAction
    | SetPageSizeAction
    | SetSortAction
    | SetFilterAction
    | setUpdatePage
    | setStocks
    | setOpenFilterDrawer
    | setShowFilterDrawer
    | setTotal
    | setFilters
    | setModelFilter
    | setSubmodelFilter
    | setRangeVehiclePricesFilter
    | setRentalPeriod
    | setStartTime
    | setEndTime;

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'setPage':
            return { ...state, page: action.page };

        case 'setPageSize':
            return { ...state, page: 1, pageSize: action.pageSize };

        case 'setSort':
            return { ...state, page: 1, sort: action.sortBy };

        case 'setFilter':
            return { ...state, page: 1, filter: action.filteringRule };

        case 'setScroll':
            return { ...state, scroll: action.scroll };

        case 'setStocks':
            return { ...state, stocks: action.stocks };

        case 'setOpenFilterDrawer':
            return { ...state, openFilterDrawer: action.openFilterDrawer };

        case 'setShowFilterDrawer':
            return { ...state, showFilterDrawer: action.showFilterDrawer };

        case 'setTotal':
            return { ...state, total: action.total };

        case 'setFilters':
            return { ...state, filters: action.filters };

        case 'setModelFilter':
            return { ...state, modelFilter: action.modelFilter };

        case 'setSubmodelFilter':
            return { ...state, submodelFilter: action.submodelFilter };

        case 'setRangeVehiclePricesFilter':
            return { ...state, rangeVehiclePricesFilter: action.rangeVehiclePricesFilter };

        case 'setRentalPeriod':
            return { ...state, rentalPeriod: action.rentalPeriod };

        case 'setStartTime':
            return { ...state, startTime: action.startTime };

        case 'setEndTime':
            return { ...state, endTime: action.endTime };

        default:
            return state;
    }
};

/* In Mobility, Filter drawer will always be shown as it has DateTime filter to show regardless of other filters.  */
const hasDateTimeFilter = true;

export const useMobilityVehicleListReducer = () =>
    useReducer(reducer, {
        // default pagination
        page: 1,
        pageSize: 12,

        // default sorting
        sort: { field: StockSortingField.VariantOrder, order: SortingOrder.Asc },

        filter: {},
        scroll: true,
        stocks: [],

        showFilterDrawer: hasDateTimeFilter,
        openFilterDrawer: true,

        total: 0,
        filters: null,
        modelFilter: null,
        submodelFilter: null,
        rangeVehiclePricesFilter: null,
        rentalPeriod: null,
        startTime: null,
        endTime: null,
    });

export type ActionMethods = {
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    setSort: (sortBy: StockSortingRule) => void;
    setFilter: (filteringRule: LocalVariantFilteringRule) => void;
    setScroll: (scroll: boolean) => void;
    setOpenFilterDrawer: (openFilterDrawer: boolean) => void;
    setModelFilter: (modelFilter: string[]) => void;
    setSubmodelFilter: (submodelFilter: string[]) => void;
    setRangeVehiclePricesFilter: (rangeVehiclePricesFilter: string[]) => void;
    setRentalPeriod: (rentalPeriod: { start: string | Date; end: string | Date }) => void;
    setStartTime: (startTime: string | Date) => void;
    setEndTime: (endTime: string | Date) => void;
};

type Meta = {
    endpoint: MobilityApplicationEntrypointContextDataFragment;
    loading: boolean;
};

export type CarListingContext = {
    state: State;
    actions: ActionMethods;
    meta: Meta;
};

const Context = createContext<CarListingContext | null>(null);

export const useCarListingState = () => {
    const context = useContext(Context);

    if (!context) {
        throw new Error('useCarListingState must be used within a CarListingStateProvider');
    }

    return context;
};

export type CarListingStateProviderProps = {
    endpoint: MobilityApplicationEntrypointContextDataFragment;
    children: JSX.Element | React.ReactNode;
};

const CarListingStateProvider = ({ children, endpoint }: CarListingStateProviderProps) => {
    const [state, dispatch] = useMobilityVehicleListReducer();
    const rootElement = document.getElementById('root');
    const verticalScroll = useMemo(
        () => rootElement.scrollHeight > window.innerHeight + 100,
        [rootElement.scrollHeight]
    );

    const prevFilter = useRef(null);

    const navigate = useNavigate();

    const previousList = useRef<MobilityStock[]>([]);

    const actions = useMemo(
        (): ActionMethods => ({
            setPage: page => dispatch({ type: 'setPage', page }),
            setPageSize: pageSize => dispatch({ type: 'setPageSize', pageSize }),
            setSort: sortBy => dispatch({ type: 'setSort', sortBy }),
            setFilter: filteringRule => dispatch({ type: 'setFilter', filteringRule }),
            setScroll: scroll => dispatch({ type: 'setScroll', scroll }),
            setOpenFilterDrawer: openFilterDrawer => dispatch({ type: 'setOpenFilterDrawer', openFilterDrawer }),
            setModelFilter: modelFilter => dispatch({ type: 'setModelFilter', modelFilter }),
            setSubmodelFilter: submodelFilter => dispatch({ type: 'setSubmodelFilter', submodelFilter }),
            setRangeVehiclePricesFilter: rangeVehiclePricesFilter =>
                dispatch({ type: 'setRangeVehiclePricesFilter', rangeVehiclePricesFilter }),
            setRentalPeriod: rentalPeriod => dispatch({ type: 'setRentalPeriod', rentalPeriod }),
            setStartTime: startTime => dispatch({ type: 'setStartTime', startTime }),
            setEndTime: endTime => dispatch({ type: 'setEndTime', endTime }),
        }),
        [dispatch]
    );

    const { data, loading } = useListMobilityStocksQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                moduleId: endpoint.mobilityApplicationModule.id,
                kind: InventoryKind.MobilityInventory,
                modelNames: state.modelFilter,
                submodelNames: state.submodelFilter,
                rangeVehiclePrices:
                    state?.rangeVehiclePricesFilter?.length !== state?.filters?.dailyRates?.length
                        ? state?.rangeVehiclePricesFilter?.map(rate => {
                              const [fromStr, toStr] = rate.split('-');

                              return {
                                  from: parseFloat(fromStr),
                                  to: parseFloat(toStr),
                              };
                          })
                        : null,
                productionOnly: true,
            },
            pagination: { offset: (state.page - 1) * state.pageSize, limit: state.pageSize },
            sort: state.sort,
        },
    });

    const context = useMemo(
        (): CarListingContext => ({ state, actions, meta: { endpoint, loading } }),
        [state, actions, endpoint, loading]
    );

    const { data: filtersData } = useGetMobilityVehicleFiltersQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            moduleId: endpoint.mobilityApplicationModule.id,
            productionOnly: true,
        },
    });

    useEffect(() => {
        if (data?.list?.items && !loading) {
            previousList.current = uniqBy('id', concat(previousList.current, data.list.items));
            dispatch({ type: 'setStocks', stocks: previousList.current });

            if (!state.scroll) {
                dispatch({ type: 'setScroll', scroll: true });
            }
        }

        if (isFinite(data?.list?.count)) {
            dispatch({ type: 'setTotal', total: data.list.count });
        }

        // have only 1 variant of the listing should directly navigate to the details
        if (data?.list?.count === 1 && !loading) {
            const stock = data?.list?.items[0];
            if (!isNil(stock) && stock.__typename !== 'MobilityStockInventory') {
                throw new Error('MobilityStock not found');
            }

            if (!prevFilter.current) {
                navigate(`./details/${stock.id}`, {
                    state: {
                        rentalPeriod: { start: null, end: null },
                        startTime: null,
                        endTime: null,
                        isOnlyOneVariant: true,
                    },
                });
            }
        }
        // prevent auto navigate when filter being applied
        if (!loading && data?.list.count > 1) {
            prevFilter.current = {
                modelFilter: state.modelFilter,
                submodelFilter: state.submodelFilter,
                rangeVehiclePricesFilter: state.rangeVehiclePricesFilter,
            };
        }
    }, [
        data,
        dispatch,
        loading,
        navigate,
        state.modelFilter,
        state.rangeVehiclePricesFilter,
        state.scroll,
        state.submodelFilter,
    ]);

    useEffect(() => {
        if (!state.scroll) {
            dispatch({ type: 'setStocks', stocks: [] });
            previousList.current = [];
        }
    }, [dispatch, state.scroll]);

    useEffect(() => {
        if (filtersData?.filters) {
            dispatch({ type: 'setFilters', filters: filtersData.filters });
        }
    }, [filtersData, dispatch]);

    useEffect(() => {
        if (!verticalScroll && previousList.current.length !== 0 && previousList.current.length < state.total) {
            dispatch({ type: 'setPageSize', pageSize: state.pageSize + 4 });
        }
    }, [dispatch, state.pageSize, state.total, verticalScroll]);

    return (
        <Context.Provider value={context}>
            {state.scroll && loading && state.stocks.length === 0 ? <PortalLoadingElement /> : children}
        </Context.Provider>
    );
};

export default CarListingStateProvider;
